/**
 * Zustand store for global state management
 * Session persistence uses zustand persist middleware with localStorage
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Track hydration state for auth guard
let hasHydrated = false;

// Auth store with improved persistence
export const useAuthStore = create(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      userType: null, // 'admin' | 'delegate' | 'member' | 'student' | 'teacher' | etc.
      // True whenever a token is present. Exposed as a plain field so
      // components can destructure `const { isAuthenticated } = useAuthStore()`
      // — historically several Bible pages did this against a non-existent
      // field, which always read undefined and produced the spurious
      // "Sign in to track reading progress" prompt for already-logged-in
      // students.
      isAuthenticated: false,
      eventId: null,
      permissions: [],
      _hasHydrated: false, // Internal flag for hydration tracking
      
      // Unified auth setter - avoid manual localStorage writes
      setAuth: (token, user, userType, eventId = null, permissions = []) => {
        set({ token, user, userType, eventId, permissions, isAuthenticated: !!token });
        // Keep legacy flat key in sync so components reading `eventflow_token`
        // directly continue to work (esp. school admins coming from per-school login).
        try {
          if (token) localStorage.setItem('eventflow_token', token);
          else localStorage.removeItem('eventflow_token');
        } catch (_e) { /* ignore */ }
      },
      
      logout: () => {
        set({ token: null, user: null, userType: null, eventId: null, permissions: [], isAuthenticated: false });
        try { localStorage.removeItem('eventflow_token'); } catch (_e) { /* ignore */ }
      },
      
      // Exit current event context without logging out completely
      exitEvent: () => {
        set({ eventId: null });
      },
      
      // Set current event context
      setEventId: (eventId) => {
        set({ eventId });
      },
      
      isAdmin: () => get().userType === 'admin',
      isDelegate: () => get().userType === 'delegate',
      
      // Enhanced permission checking with wildcard and category support
      hasPermission: (permission) => {
        const perms = get().permissions || [];
        const user = get().user;
        
        // Super admin has full access
        if (perms.includes('*') || user?.role === 'super_admin') return true;
        
        // Exact match
        if (perms.includes(permission)) return true;
        
        // Category wildcard (e.g., user has "delegates.*" and checking "delegates.view")
        if (permission.includes('.')) {
          const category = permission.split('.')[0];
          if (perms.includes(`${category}.*`)) return true;
        }
        
        return false;
      },
      
      // Check if user has ANY of the specified permissions
      hasAnyPermission: (permissions) => {
        const check = get().hasPermission;
        return permissions.some(p => check(p));
      },
      
      // Check if user has ALL of the specified permissions  
      hasAllPermissions: (permissions) => {
        const check = get().hasPermission;
        return permissions.every(p => check(p));
      },
      
      // Get user's role
      getRole: () => {
        const user = get().user;
        return user?.role || 'guest';
      },
      
      // Check if user is super admin
      isSuperAdmin: () => {
        const user = get().user;
        const perms = get().permissions || [];
        return user?.role === 'super_admin' || perms.includes('*');
      },
      
      // Hydration complete marker
      setHasHydrated: (state) => {
        hasHydrated = state;
        set({ _hasHydrated: state });
      },
    }),
    {
      name: 'eventflow-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ 
        token: state.token, 
        user: state.user, 
        userType: state.userType,
        eventId: state.eventId,
        permissions: state.permissions,
        isAuthenticated: !!state.token,
      }),
      onRehydrateStorage: () => (state) => {
        // Called when storage is rehydrated. Re-derive isAuthenticated from
        // the token so a page refresh doesn't drop the "logged-in" badge.
        hasHydrated = true;
        if (state) {
          state.isAuthenticated = !!state.token;
          state.setHasHydrated?.(true);
        }
      },
    }
  )
);

// Utility to check if store has hydrated (for auth guards)
export const hasAuthHydrated = () => hasHydrated;

// Event store for currently viewed/managed event
export const useEventStore = create((set) => ({
  currentEvent: null,
  branding: null,
  tiers: [],
  
  setCurrentEvent: (event) => set({ 
    currentEvent: event,
    branding: event?.branding || null,
    tiers: event?.ticket_tiers || []
  }),
  
  updateBranding: (branding) => set({ branding }),
  updateTiers: (tiers) => set({ tiers }),
  clearEvent: () => set({ currentEvent: null, branding: null, tiers: [] }),
}));

// Real-time updates store
export const useRealtimeStore = create((set, get) => ({
  connected: false,
  updates: [],
  
  setConnected: (connected) => set({ connected }),
  
  addUpdate: (update) => set((state) => ({
    updates: [update, ...state.updates].slice(0, 100)
  })),
  
  clearUpdates: () => set({ updates: [] }),
}));

// Demo mode store (for offline/demo functionality)
export const useDemoStore = create(
  persist(
    (set, get) => ({
      isDemoMode: false,
      demoEvents: [],
      demoDelegates: [],
      demoTransactions: [],
      
      setDemoMode: (enabled) => set({ isDemoMode: enabled }),
      
      // Demo data operations
      addDemoEvent: (event) => set((state) => ({
        demoEvents: [...state.demoEvents, event]
      })),
      
      addDemoDelegate: (delegate) => set((state) => ({
        demoDelegates: [...state.demoDelegates, delegate]
      })),
      
      updateDemoDelegate: (id, updates) => set((state) => ({
        demoDelegates: state.demoDelegates.map(d => 
          d.id === id ? { ...d, ...updates } : d
        )
      })),
      
      clearDemoData: () => set({
        demoEvents: [],
        demoDelegates: [],
        demoTransactions: []
      }),
    }),
    {
      name: 'eventflow-demo',
    }
  )
);

// UI state store
export const useUIStore = create((set) => ({
  sidebarOpen: true,
  theme: 'light',
  notifications: [],
  
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  setTheme: (theme) => set({ theme }),
  
  addNotification: (notification) => set((state) => ({
    notifications: [...state.notifications, { id: Date.now(), ...notification }]
  })),
  
  removeNotification: (id) => set((state) => ({
    notifications: state.notifications.filter(n => n.id !== id)
  })),
  
  clearNotifications: () => set({ notifications: [] }),
}));
